import React from 'react';
import {
  IonIcon,
  IonButton,
} from '@ionic/react';
import { checkmarkCircle, refreshCircle } from 'ionicons/icons';
import styles from './UploadProgress.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  timeRemaining?: number;
  uploadStartTime?: number;
  bytesUploaded?: number;
  compressedSize?: number;
}

interface UploadProgressProps {
  uploadingFiles: UploadingFile[];
  isVisible: boolean;
  onCancel: (index: number) => void;
  onRemove: (index: number) => void;
  onRetry: (index: number) => void;
  onClearCompleted?: () => void;
  onPause?: () => void;
  onCancelAll?: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return i18n.t('components.UploadProgress.0Bytes');
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + ' ' + sizes[i];
};

const getThumbnailUrl = (file: File): string => {
  if (file.type.startsWith('image/')) {
    return URL.createObjectURL(file);
  }
  return '';
};

const getFileIcon = (file: File): string => {
  const type = file.type;
  if (type.startsWith('image/')) return '🖼️';
  if (type.startsWith('video/')) return '🎬';
  if (type.startsWith('audio/')) return '🎵';
  if (type.includes('pdf')) return '📄';
  return '📎';
};

export const UploadProgress: React.FC<UploadProgressProps> = ({
  uploadingFiles,
  isVisible,
  onRetry,
 
}) => {
  const { t } = useTranslation();
  if (!isVisible || uploadingFiles.length === 0) {
    return null;
  }

  return (
    <div className={styles['upload-progress-container']}>
      {/* Header */}
      <div className={styles['upload-header']}>
        <h3 className={styles['upload-title']}>{t('components.UploadProgress.uploadingFiles', { length: uploadingFiles.length })}</h3>
      </div>

      {/* File List */}
      <div className={styles['file-list']}>
        {uploadingFiles.map((uploadFile, idx) => (
          <div key={idx} className={styles['file-item']}>
            <div className={styles['file-thumbnail']}>
              {uploadFile.file.type.startsWith('image/') ? (
                <img src={getThumbnailUrl(uploadFile.file)} alt={uploadFile.file.name} />
              ) : (
                <span>{getFileIcon(uploadFile.file)}</span>
              )}
            </div>
            <div className={styles['file-info']}>
              <p className={styles['file-name']}>{uploadFile.file.name}</p>
              {uploadFile.status === 'error' && uploadFile.error ? (
                <p className={styles['file-error']}>{uploadFile.error}</p>
              ) : (
                <p className={styles['file-meta']}>
                  <span>{formatFileSize(uploadFile.compressedSize ?? uploadFile.file.size)}</span>
                </p>
              )}
            </div>
            <div className={styles['file-status']}>
              {uploadFile.status === 'completed' && (
                <IonIcon icon={checkmarkCircle} className={styles['status-complete']} />
              )}
              {uploadFile.status === 'uploading' && (
                <div className={styles['status-spinner']} />
              )}
              {uploadFile.status === 'error' && (
                <IonButton fill="clear" size="small" onClick={() => onRetry(idx)} title={uploadFile.error}>
                  <IonIcon icon={refreshCircle} />
                </IonButton>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
